import React, { useEffect, useMemo, useRef, useState } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { FaSearch, FaSyncAlt, FaTimes } from "react-icons/fa";
import apiService from "../../../services/apiService";
import Loader from "../../../components/ui/Loader";
import { useToast } from "../../../context/ToastContext";
import useAuthStore from "../../../store/authStore";
const AI_QUESTION_POPULATE = "qSet";

const defaultFilter = {
  search: "",
  course: "",
  stream: "",
  subject: "",
  subjectunit: "",
  topic: "",
  status: "",
};

const buildQueryString = (params = {}) => {
  const query = [];

  Object.keys(params).forEach((key) => {
    const value = params[key];

    if (value === undefined || value === null || value === "") return;

    // populate comma separated hi jana chahiye
    if (key === "populate") {
      query.push(`${encodeURIComponent(key)}=${String(value)}`);
      return;
    }

    query.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    );
  });

  return query.join("&");
};

const getRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.records)) return response.records;
  return [];
};

const getRecord = (response) => {
  if (!response) return null;
  if (Array.isArray(response)) return response[0] || null;
  if (response?.data?.data && !Array.isArray(response.data.data)) {
    return response.data.data;
  }
  if (response?.data && !Array.isArray(response.data)) return response.data;
  if (Array.isArray(response?.data)) return response.data[0] || null;
  if (response?.record) return response.record;
  if (response?.result) return response.result;
  if (response?.id || response?._id) return response;
  return null;
};

const getId = (item) => {
  return item?.id || item?._id || "";
};

const getRefId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.id || value._id || value.$oid || "";
};

const getName = (item) => {
  if (!item) return "-";

  if (typeof item === "string") return item;

  return (
    item.name ||
    item.title ||
    item.value ||
    item.valueAlias ||
    item.code ||
    item.subjectName ||
    item.topicName ||
    item.unitName ||
    item.id ||
    "-"
  );
};

const getQSetIdFromRow = (row = {}) => {
  return (
    getRefId(row?.qSet) ||
    getRefId(row?.qSetId) ||
    getRefId(row?.qset) ||
    getRefId(row?.qsetId) ||
    getRefId(row?.qSet_id) ||
    getRefId(row?.qset_id) ||
    ""
  );
};

const IMAGE_FILE_NAME_REGEX =
  /(?:solution-)?image-[A-Za-z0-9._-]+\.(?:png|jpe?g|webp|gif)/i;

const getImageFileName = (value) => {
  const source = String(value || "")
    .trim()
    .replace(/&amp;/gi, "&")
    .replace(/\\+&/g, "&");

  if (!source) return "";

  const directMatch = source.match(IMAGE_FILE_NAME_REGEX);

  if (directMatch) {
    return directMatch[0];
  }

  try {
    const parsedUrl = new URL(source);
    const pathname = decodeURIComponent(parsedUrl.pathname || "");
    const baseName = pathname.split("/").filter(Boolean).pop() || "";
    const fileMatch = baseName.match(IMAGE_FILE_NAME_REGEX);

    return fileMatch ? fileMatch[0] : "";
  } catch (error) {
    return "";
  }
};

const buildS3ImageUrl = (imageValue, row = {}, client = {}) => {
  const source = String(imageValue || "").trim();

  if (!source) return "";

  if (/^https?:\/\//i.test(source)) {
    return source;
  }

  const imageName = getImageFileName(source);

  if (!imageName) {
    return source;
  }

  const s3Dir = String(client?.s3Dir || "").replace(/^\/+|\/+$/g, "");
  const qSetId = getQSetIdFromRow(row);

  if (!s3Dir || !qSetId) {
    console.log("S3 image url missing data:", {
      imageName,
      s3Dir,
      qSetId,
      row,
      client,
    });

    return imageName;
  }

  return `https://s3.us-west-004.backblazeb2.com/s3file/${s3Dir}/QSet/${qSetId}/${imageName}`;
};

const cleanUnsafeHtml = (html = "") => {
  return String(html)
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/\son\w+=["'][^"']*["']/gi, "")
    .replace(/\sjavascript:/gi, "");
};

const countUnescapedDollarSignsForList = (value) => {
  const text = String(value || "");
  let count = 0;

  for (let index = 0; index < text.length; index += 1) {
    if (text[index] !== "$") continue;

    let slashCount = 0;
    let cursor = index - 1;

    while (cursor >= 0 && text[cursor] === "\\") {
      slashCount += 1;
      cursor -= 1;
    }

    if (slashCount % 2 === 0) count += 1;
  }

  return count;
};

const repairMalformedLatexForList = (value) => {
  let text = String(value || "")
    .replace(/\\mathmr\b/g, "\\mathrm")
    .replace(/\\rm\s*\{/g, "\\mathrm{")
    .replace(/\\left(?![A-Za-z])\s*/g, "")
    .replace(/\\right(?![A-Za-z])\s*/g, "")
    .replace(/\\(?:righ|rig|ri|r|lef|le|l)(?=\s*(?:\$|\\\)|\\\]|<|$))/g, "")
    .replace(
      /(?:Extra\s+\\left\s+or\s+missing\s+\\right|Missing\s+\\right|Extra\s+\\right)/gi,
      "",
    );

  if (countUnescapedDollarSignsForList(text) % 2 !== 0) {
    text += "$";
  }

  return text;
};

const normalizeLatex = (value = "") => {
  return repairMalformedLatexForList(value);
};

const renderLatexExpression = (expression, displayMode = false) => {
  try {
    return katex.renderToString(normalizeLatex(expression).trim(), {
      throwOnError: false,
      displayMode,
      strict: "ignore",
      trust: false,
      output: "html",
    });
  } catch (error) {
    console.warn("KaTeX render failed:", expression, error);
    return expression;
  }
};

const renderMathInText = (value = "") => {
  let text = normalizeLatex(value);

  text = text.replace(/\$\$([\s\S]*?)\$\$/g, (match, expression) =>
    renderLatexExpression(expression, true),
  );

  text = text.replace(/\\\[([\s\S]*?)\\\]/g, (match, expression) =>
    renderLatexExpression(expression, true),
  );

  text = text.replace(/\\\(([\s\S]*?)\\\)/g, (match, expression) =>
    renderLatexExpression(expression, false),
  );

  text = text.replace(/\$([^$\n]+?)\$/g, (match, expression) =>
    renderLatexExpression(expression, false),
  );

  return text;
};

const escapeHtmlAttribute = (value) => {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

const renderTextWithTags = (html = "", row = {}, client = {}) => {
  if (!html) return "-";

  let text = cleanUnsafeHtml(html);

  const placeholders = [];
  const seenSources = new Set();

  const pushImage = (rawSource, alt = "Question image") => {
    const source = String(rawSource || "").trim();

    if (!source) return "";

    const finalSource = buildS3ImageUrl(source, row, client);

    if (!finalSource) return "";

    const dedupeKey = /^https?:\/\//i.test(finalSource)
      ? finalSource
      : getImageFileName(finalSource) || finalSource;

    if (seenSources.has(dedupeKey)) {
      return "";
    }

    seenSources.add(dedupeKey);

    const placeholder = `@@AI_QUESTION_IMAGE_${placeholders.length}@@`;

    placeholders.push(
      `<img src="${escapeHtmlAttribute(
        finalSource,
      )}" alt="${escapeHtmlAttribute(
        alt || "Question image",
      )}" class="inline-block max-w-full h-auto my-2 align-middle" />`,
    );

    return placeholder;
  };

  /*
   * IMPORTANT ORDER:
   * 1. Markdown image
   * 2. Existing <img>
   * 3. Full image URL
   * 4. Bare image filename
   *
   * Full URL must be processed before bare filename. Otherwise only
   * image-xxx.png inside the URL becomes an <img> and the URL prefix remains
   * visible in the page.
   */
  text = text.replace(
    /!\[([^\]]*)\]\s*\(\s*(https?:\/\/[^)\s]+?\.(?:png|jpg|jpeg|webp|gif)(?:\?[^)\s]*)?)\s*\)/gi,
    (match, alt, src) => pushImage(src, alt || "Question image"),
  );

  text = text.replace(
    /!\[([^\]]*)\]\s*\(\s*((?:solution-)?image-[\w.-]+\.(?:png|jpg|jpeg|webp|gif))\s*\)/gi,
    (match, alt, imageName) => pushImage(imageName, alt || imageName),
  );

  text = text.replace(
    /<img\b([^>]*?)\bsrc=["']([^"']+)["']([^>]*)>/gi,
    (match, before, oldSrc, after) => {
      const imageName = getImageFileName(oldSrc);

      if (!/^https?:\/\//i.test(oldSrc) && !imageName) {
        return match;
      }

      return pushImage(oldSrc, imageName || "Question image");
    },
  );

  text = text.replace(
    /https?:\/\/[^\s<>"')]+?\.(?:png|jpg|jpeg|webp|gif)(?:\?[^\s<>"')]*)?/gi,
    (fullUrl) => pushImage(fullUrl, "Question image"),
  );

  text = text.replace(
    /(?:solution-)?image-[A-Za-z0-9._-]+\.(?:png|jpe?g|webp|gif)/gi,
    (imageName) => pushImage(imageName, imageName),
  );

  placeholders.forEach((imageHtml, index) => {
    text = text.replaceAll(`@@AI_QUESTION_IMAGE_${index}@@`, imageHtml);
  });

  return renderMathInText(
    text
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/\n/g, "<br/>")
      .trim(),
  );
};

const normalizeImageField = (value) => {
  if (!value) return [];

  const source = Array.isArray(value) ? value.join("\n") : String(value);

  const matches = [];

  const markdownRegex = /!\[[^\]]*\]\s*\(\s*([^)]+)\s*\)/gi;
  let match;

  while ((match = markdownRegex.exec(source)) !== null) {
    matches.push(match[1]);
  }

  const htmlRegex = /<img\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/gi;

  while ((match = htmlRegex.exec(source)) !== null) {
    matches.push(match[1]);
  }

  const urlRegex =
    /https?:\/\/[^\s<>"')]+?\.(?:png|jpg|jpeg|webp|gif)(?:\?[^\s<>"')]*)?/gi;

  while ((match = urlRegex.exec(source)) !== null) {
    matches.push(match[0]);
  }

  const fileRegex =
    /(?:solution-)?image-[A-Za-z0-9._-]+\.(?:png|jpe?g|webp|gif)/gi;

  while ((match = fileRegex.exec(source)) !== null) {
    matches.push(match[0]);
  }

  return Array.from(
    new Set(matches.map((item) => String(item || "").trim()).filter(Boolean)),
  );
};

const normalizeOptions = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item === "string" || typeof item === "number") {
          return String(item).trim();
        }
        return String(
          item.text ||
            item.value ||
            item.option ||
            item.label ||
            item.content ||
            "",
        ).trim();
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return normalizeOptions(parsed);
    } catch (error) {}

    return trimmed
      .split(/\r?\n|\s*\|\|\s*/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const ensureOptionLabel = (option, index) => {
  const text = collapseDuplicateOptionLabel(option);

  if (!text) return "";
  if (PRINTED_CHOICE_LABEL_REGEX.test(text)) {
    return text;
  }

  return `(${index + 1}) ${text}`;
};

const PRINTED_CHOICE_LABEL_REGEX =
  /^\s*(?:\(\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*\)|\[\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*\]|(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*[\).,:;\-])\s*/i;

const collapseDuplicateOptionLabel = (value) => {
  const text = String(value || "").trim();
  const label =
    "(?:\\(\\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*\\)|" +
    "\\[\\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*\\]|" +
    "(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*[\\).,:;\\-])";
  const match = text.match(
    new RegExp(`^\\s*(${label})\\s+(${label})\\s+`, "i"),
  );

  if (!match) return text;

  const first = String(match[1] || "")
    .replace(/\s+/g, "")
    .toLowerCase();
  const second = String(match[2] || "")
    .replace(/\s+/g, "")
    .toLowerCase();

  return first === second
    ? `${match[1]} ${text.slice(match[0].length)}`.trim()
    : text;
};

const AiQuestionList = () => {
  const { showError } = useToast();
  const { client } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [optionLoading, setOptionLoading] = useState(false);

  const [questions, setQuestions] = useState([]);

  const [courses, setCourses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [subjectUnits, setSubjectUnits] = useState([]);
  const [topics, setTopics] = useState([]);

  const referenceCacheRef = useRef({
    course: {},
    stream: {},
    subject: {},
    subjectunit: {},
    topic: {},
  });

  const referencePromiseRef = useRef({});

  const [referenceNames, setReferenceNames] = useState({
    course: {},
    stream: {},
    subject: {},
    subjectunit: {},
    topic: {},
  });

  const [filter, setFilter] = useState(defaultFilter);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(20);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / pageLimit), 1);
  }, [total, pageLimit]);

  useEffect(() => {
    loadInitialOptions();
    loadQuestions(defaultFilter, 1, pageLimit);
  }, []);

  useEffect(() => {
    loadQuestions(filter, page, pageLimit);
  }, [page, pageLimit]);

  useEffect(() => {
    if (filter.stream) {
      loadSubjects(filter.stream);
    } else {
      setSubjects([]);
      setSubjectUnits([]);
      setTopics([]);
    }

    setFilter((prev) => ({
      ...prev,
      subject: "",
      subjectunit: "",
      topic: "",
    }));
  }, [filter.stream]);

  useEffect(() => {
    if (filter.subject) {
      loadSubjectUnits(filter.subject);
      loadTopics(filter.subject, "");
    } else {
      setSubjectUnits([]);
      setTopics([]);
    }

    setFilter((prev) => ({
      ...prev,
      subjectunit: "",
      topic: "",
    }));
  }, [filter.subject]);

  useEffect(() => {
    if (filter.subject) {
      loadTopics(filter.subject, filter.subjectunit);
    }
  }, [filter.subjectunit]);

  const mergeReferenceRows = (type, rows = []) => {
    const cache = referenceCacheRef.current[type] || {};

    (rows || []).forEach((row) => {
      const id = String(getId(row) || "");
      if (!id) return;

      cache[id] = getName(row);
    });

    referenceCacheRef.current[type] = cache;

    setReferenceNames((prev) => ({
      ...prev,
      [type]: { ...cache },
    }));
  };

  const fetchReferenceRecord = async (type, resource, id) => {
    const finalId = String(id || "");
    if (!finalId) return null;

    if (referenceCacheRef.current[type]?.[finalId]) {
      return referenceCacheRef.current[type][finalId];
    }

    const promiseKey = `${type}:${finalId}`;

    if (!referencePromiseRef.current[promiseKey]) {
      referencePromiseRef.current[promiseKey] = (async () => {
        let record = null;

        try {
          const response = await apiService.request_old(
            `/i/ims/${resource}/${encodeURIComponent(finalId)}`,
            { method: "GET" },
          );

          record = getRecord(response);
        } catch (findOneError) {
          try {
            const response = await apiService.request_old(
              `/i/ims/${resource}?${buildQueryString({
                id: finalId,
                limit: 1,
              })}`,
              { method: "GET" },
            );

            record = getRecord(response) || getRows(response)[0] || null;
          } catch (findError) {
            console.log(`${resource.toUpperCase()} NAME LOAD FAILED:`, {
              id: finalId,
              findOneError,
              findError,
            });
          }
        }

        if (record) {
          mergeReferenceRows(type, [record]);
          return getName(record);
        }

        return "";
      })().finally(() => {
        delete referencePromiseRef.current[promiseKey];
      });
    }

    return referencePromiseRef.current[promiseKey];
  };

  const loadReferenceNames = async (rows = []) => {
    const references = {
      course: {
        resource: "coption",
        ids: rows.map((row) => getRefId(row?.course)),
      },
      stream: {
        resource: "coption",
        ids: rows.map((row) => getRefId(row?.stream)),
      },
      subject: {
        resource: "subject",
        ids: rows.map((row) => getRefId(row?.subject)),
      },
      subjectunit: {
        resource: "subjectunit",
        ids: rows.map((row) =>
          getRefId(row?.subjectunit || row?.subjectUnit || row?.unit),
        ),
      },
      topic: {
        resource: "topic",
        ids: rows.map((row) => getRefId(row?.topic)),
      },
    };

    const requests = [];

    Object.entries(references).forEach(([type, config]) => {
      const uniqueIds = Array.from(
        new Set((config.ids || []).filter(Boolean).map(String)),
      );

      uniqueIds.forEach((id) => {
        if (!referenceCacheRef.current[type]?.[id]) {
          requests.push(fetchReferenceRecord(type, config.resource, id));
        }
      });
    });

    if (requests.length) {
      await Promise.allSettled(requests);
    }
  };

  const getReferenceName = (type, value, fallback = "") => {
    if (!value) return fallback || "-";

    if (typeof value === "object") {
      return getName(value);
    }

    const id = String(getRefId(value) || value || "");

    return (
      referenceNames?.[type]?.[id] ||
      referenceCacheRef.current?.[type]?.[id] ||
      fallback ||
      id ||
      "-"
    );
  };

  const loadInitialOptions = async () => {
    try {
      setOptionLoading(true);

      const courseApi = `/i/ims/coption?${buildQueryString({
        option: "COURSE",
        sort: "seq ASC",
        limit: "all",
        status: true,
      })}`;

      const streamApi = `/i/ims/coption?${buildQueryString({
        option: "STREAM",
        sort: "seq ASC",
        limit: "all",
        status: true,
      })}`;

      const [courseRes, streamRes] = await Promise.all([
        apiService.request_old(courseApi, { method: "GET" }),
        apiService.request_old(streamApi, { method: "GET" }),
      ]);

      const courseRows = getRows(courseRes);
      const streamRows = getRows(streamRes);

      setCourses(courseRows);
      setStreams(streamRows);

      mergeReferenceRows("course", courseRows);
      mergeReferenceRows("stream", streamRows);
    } catch (err) {
      console.log(err);
      showError("Filter options load failed.");
    } finally {
      setOptionLoading(false);
    }
  };
  const loadSubjects = async (streamId) => {
    try {
      const api = `/i/ims/subject?${buildQueryString({
        stream: streamId,
        limit: "all",
        populate: "stream",
      })}`;

      const res = await apiService.request_old(api, {
        method: "GET",
      });

      const rows = getRows(res);
      setSubjects(rows);
      mergeReferenceRows("subject", rows);
    } catch (err) {
      console.log(err);
      showError("Subjects load failed.");
    }
  };

  const loadSubjectUnits = async (subjectId) => {
    try {
      const api = `/i/ims/subjectunit?${buildQueryString({
        subject: subjectId,
        limit: "all",
        populate: "subject",
      })}`;

      const res = await apiService.request_old(api, {
        method: "GET",
      });

      const rows = getRows(res);
      setSubjectUnits(rows);
      mergeReferenceRows("subjectunit", rows);
    } catch (err) {
      console.log(err);
      setSubjectUnits([]);
    }
  };

  const loadTopics = async (subjectId, subjectunitId) => {
    try {
      const api = `/i/ims/topic?${buildQueryString({
        subject: subjectId,
        subjectunit: subjectunitId,
        limit: "all",
        populate: "subject,subjectunit",
      })}`;

      const res = await apiService.request_old(api, {
        method: "GET",
      });

      const rows = getRows(res);
      setTopics(rows);
      mergeReferenceRows("topic", rows);
    } catch (err) {
      console.log(err);
      setTopics([]);
    }
  };

  const buildQuestionApi = (
    customFilter = filter,
    customPage = page,
    customLimit = pageLimit,
  ) => {
    const params = {
      page: customPage,
      limit: customLimit,
      totalCount: true,
      populate: AI_QUESTION_POPULATE,
    };

    Object.keys(customFilter).forEach((key) => {
      if (customFilter[key]) {
        params[key] = customFilter[key];
      }
    });

    return `/i/ims/aiquestion?${buildQueryString(params)}`;
  };

  const loadQuestions = async (
    customFilter = filter,
    customPage = page,
    customLimit = pageLimit,
  ) => {
    try {
      setLoading(true);

      const res = await apiService.request(
        buildQuestionApi(customFilter, customPage, customLimit),
        { method: "GET" },
      );

      const rows = getRows(res);

      const totalCount =
        res?.totalCount ||
        res?.data?.totalCount ||
        res?.total ||
        res?.data?.total ||
        res?.count ||
        res?.data?.count ||
        rows.length;

      setQuestions(rows);
      setTotal(Number(totalCount || 0));

      await loadReferenceNames(rows);
    } catch (err) {
      console.log(err);
      showError(err?.message || "AI questions load failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilter = () => {
    setPage(1);
    loadQuestions(filter, 1, pageLimit);
  };

  const resetFilter = () => {
    const cleanFilter = { ...defaultFilter };

    setFilter(cleanFilter);
    setSubjects([]);
    setSubjectUnits([]);
    setTopics([]);
    setPage(1);

    loadQuestions(cleanFilter, 1, pageLimit);
  };

  const renderQuestionText = (html, row = {}) => {
    if (!html) return "-";

    return (
      <div
        className="question-html max-w-none overflow-visible whitespace-normal"
        style={{ maxHeight: "none", overflow: "visible" }}
        dangerouslySetInnerHTML={{
          __html: renderTextWithTags(html, row, client),
        }}
      />
    );
  };

  const renderQuestionImages = (row = {}, language = "eng") => {
    const preferredField = language === "hin" ? row?.image_hin : row?.image_eng;

    let imageValues = normalizeImageField(preferredField);

    if (!imageValues.length) {
      imageValues = normalizeImageField(row?.image);
    }

    if (!imageValues.length && language === "hin" && row?.question_hin) {
      imageValues = normalizeImageField(row?.image_eng);
    }

    if (!imageValues.length && language === "eng" && row?.question_eng) {
      imageValues = normalizeImageField(row?.image_hin);
    }

    const inlineImageValues = new Set(
      normalizeImageField(
        [
          language === "hin" ? row?.question_hin : row?.question_eng,
          ...normalizeOptions(
            language === "hin" ? row?.options_hin : row?.options_eng,
          ),
        ].join("\n"),
      ),
    );

    imageValues = imageValues.filter(
      (imageValue) => !inlineImageValues.has(imageValue),
    );

    if (!imageValues.length) return null;

    return (
      <div className="mt-2 space-y-2">
        {imageValues.map((imageValue, index) => {
          const src = buildS3ImageUrl(imageValue, row, client);

          if (!src) return null;

          return (
            <img
              key={`${language}-question-image-${index}-${imageValue}`}
              src={src}
              alt={`Question ${language} ${index + 1}`}
              className="inline-block max-w-full h-auto my-2 align-middle"
              loading="lazy"
            />
          );
        })}
      </div>
    );
  };

  const renderOptions = (options, row = {}, language = "eng") => {
    const optionList = normalizeOptions(options);
    if (!optionList.length) return null;

    return (
      <div
        className={`mt-2 space-y-1 ${
          language === "hin" ? "text-gray-500" : "text-gray-800"
        }`}
      >
        {optionList.map((option, index) => (
          <div key={`${language}-${index}`} className="flex items-start gap-1">
            {renderQuestionText(ensureOptionLabel(option, index), row)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-5">
        <div>
          <h1 className="text-2xl font-bold">AI Questions</h1>
          <p className="text-sm text-gray-500">Total Records: {total}</p>
        </div>

        <button
          type="button"
          onClick={() => loadQuestions(filter, page, pageLimit)}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-gray-800 text-white disabled:bg-gray-400"
        >
          <FaSyncAlt className="h-3 w-3" />
          Refresh
        </button>
      </div>

      {/* Filter section agar chahiye to uncomment kar lena */}
      {/* 
      <div className="bg-white rounded-lg shadow-md p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm mb-1">Search</label>
            <input
              type="text"
              value={filter.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="Question / answer search"
              className="w-full border rounded p-2"
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Course</label>
            <select
              value={filter.course}
              onChange={(e) => handleFilterChange("course", e.target.value)}
              className="w-full border rounded p-2"
            >
              <option value="">All Course</option>

              {courses.map((item) => (
                <option key={getId(item)} value={getId(item)}>
                  {getName(item)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Stream</label>
            <select
              value={filter.stream}
              onChange={(e) => handleFilterChange("stream", e.target.value)}
              className="w-full border rounded p-2"
            >
              <option value="">All Stream</option>

              {streams.map((item) => (
                <option key={getId(item)} value={getId(item)}>
                  {getName(item)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Subject</label>
            <select
              value={filter.subject}
              disabled={!filter.stream}
              onChange={(e) => handleFilterChange("subject", e.target.value)}
              className="w-full border rounded p-2 disabled:bg-gray-100"
            >
              <option value="">All Subject</option>

              {subjects.map((item) => (
                <option key={getId(item)} value={getId(item)}>
                  {getName(item)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Subject Unit</label>
            <select
              value={filter.subjectunit}
              disabled={!filter.subject}
              onChange={(e) =>
                handleFilterChange("subjectunit", e.target.value)
              }
              className="w-full border rounded p-2 disabled:bg-gray-100"
            >
              <option value="">All Unit</option>

              {subjectUnits.map((item) => (
                <option key={getId(item)} value={getId(item)}>
                  {getName(item)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Topic</label>
            <select
              value={filter.topic}
              disabled={!filter.subject}
              onChange={(e) => handleFilterChange("topic", e.target.value)}
              className="w-full border rounded p-2 disabled:bg-gray-100"
            >
              <option value="">All Topic</option>

              {topics.map((item) => (
                <option key={getId(item)} value={getId(item)}>
                  {getName(item)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Status</label>
            <select
              value={filter.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="w-full border rounded p-2"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>

          <div>
            <label className="block text-sm mb-1">Table Limit</label>
            <select
              value={pageLimit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);
                setPageLimit(newLimit);
                setPage(1);
                loadQuestions(filter, 1, newLimit);
              }}
              className="w-full border rounded p-2"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-4">
          <button
            type="button"
            onClick={applyFilter}
            disabled={loading || optionLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-blue-600 text-white disabled:bg-gray-400"
          >
            <FaSearch className="h-3 w-3" />
            Search
          </button>

          <button
            type="button"
            onClick={resetFilter}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-gray-500 text-white disabled:bg-gray-400"
          >
            <FaTimes className="h-3 w-3" />
            Reset
          </button>

          {(loading || optionLoading) && <Loader />}
        </div>
      </div> 
      */}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2 text-left w-[70px]">S.No.</th>
                <th className="border p-2 text-left w-[90px]">Q.No.</th>
                <th className="border p-2 text-left min-w-[560px]">Question</th>
                <th className="border p-2 text-left min-w-[130px]">Course</th>
                <th className="border p-2 text-left min-w-[130px]">Stream</th>
                <th className="border p-2 text-left min-w-[130px]">Subject</th>
                <th className="border p-2 text-left min-w-[130px]">AiUnit</th>
                <th className="border p-2 text-left min-w-[130px]">AiTopic</th>
                <th className="border p-2 text-left min-w-[100px]">Answer</th>
                <th className="border p-2 text-left min-w-[110px]">Status</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="border p-6 text-center">
                    Loading...
                  </td>
                </tr>
              ) : questions.length === 0 ? (
                <tr>
                  <td colSpan="10" className="border p-6 text-center">
                    No AI questions found.
                  </td>
                </tr>
              ) : (
                questions.map((row, index) => {
                  const unit = row.subjectunit || row.subjectUnit || row.unit;

                  return (
                    <tr key={row.id || row._id || index}>
                      <td className="border p-2">
                        {(page - 1) * pageLimit + index + 1}
                      </td>

                      <td className="border p-2 font-semibold">
                        {row.question_num || row.questionNo || row.qNo || "-"}
                      </td>

                      <td className="border p-2 align-top">
                        {(row.question_eng ||
                          row.question ||
                          row.questionText) && (
                          <div className="font-medium">
                            {renderQuestionText(
                              row.question_eng ||
                                row.question ||
                                row.questionText,
                              row,
                            )}

                            {renderQuestionImages(row, "eng")}

                            {renderOptions(
                              row.options_eng ||
                                row.option_eng ||
                                row.englishOptions ||
                                row.optionEng ||
                                row.optionsEnglish ||
                                row.options,
                              row,
                              "eng",
                            )}
                          </div>
                        )}

                        {row.question_hin && (
                          <div className="text-gray-500 mt-3 pt-2 border-t border-gray-200">
                            {renderQuestionText(row.question_hin, row)}

                            {renderQuestionImages(row, "hin")}

                            {renderOptions(
                              row.options_hin ||
                                row.option_hin ||
                                row.hindiOptions ||
                                row.optionHin ||
                                row.optionsHindi,
                              row,
                              "hin",
                            )}
                          </div>
                        )}
                      </td>

                      <td className="border p-2">
                        {getReferenceName("course", row.course, row.courseName)}
                      </td>

                      <td className="border p-2">
                        {getReferenceName("stream", row.stream, row.streamName)}
                      </td>

                      <td className="border p-2">
                        {getReferenceName(
                          "subject",
                          row.subject,
                          row.subjectName,
                        )}
                      </td>

                      <td className="border p-2">
                        {getReferenceName(
                          "subjectunit",
                          unit,
                          row.aiUnitName || row.unitName,
                        )}
                      </td>

                      <td className="border p-2">
                        {getReferenceName(
                          "topic",
                          row.topic,
                          row.aiTopicName || row.topicName,
                        )}
                      </td>

                      <td className="border p-2 font-semibold">
                        {row.answer || row.correctOption || "-"}
                      </td>

                      <td className="border p-2">
                        {row.status === true
                          ? "Active"
                          : row.status === false
                            ? "Inactive"
                            : row.status || "-"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center p-4 border-t">
          <div className="text-sm text-gray-600">
            Page {page} / {totalPages} | Total {total}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
            >
              Previous
            </button>

            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((prev) => prev + 1)}
              className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiQuestionList;
// import React, { useEffect, useMemo, useRef, useState } from "react";
// import katex from "katex";
// import "katex/dist/katex.min.css";
// import { FaSearch, FaSyncAlt, FaTimes } from "react-icons/fa";
// import apiService from "../../../services/apiService";
// import Loader from "../../../components/ui/Loader";
// import { useToast } from "../../../context/ToastContext";
// import useAuthStore from "../../../store/authStore";
// const AI_QUESTION_POPULATE = "qSet";

// const defaultFilter = {
//   search: "",
//   course: "",
//   stream: "",
//   subject: "",
//   subjectunit: "",
//   topic: "",
//   status: "",
// };

// const buildQueryString = (params = {}) => {
//   const query = [];

//   Object.keys(params).forEach((key) => {
//     const value = params[key];

//     if (value === undefined || value === null || value === "") return;

//     // populate comma separated hi jana chahiye
//     if (key === "populate") {
//       query.push(`${encodeURIComponent(key)}=${String(value)}`);
//       return;
//     }

//     query.push(
//       `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
//     );
//   });

//   return query.join("&");
// };

// const getRows = (response) => {
//   if (Array.isArray(response)) return response;
//   if (Array.isArray(response?.data)) return response.data;
//   if (Array.isArray(response?.data?.data)) return response.data.data;
//   if (Array.isArray(response?.results)) return response.results;
//   if (Array.isArray(response?.records)) return response.records;
//   return [];
// };

// const getRecord = (response) => {
//   if (!response) return null;
//   if (Array.isArray(response)) return response[0] || null;
//   if (response?.data?.data && !Array.isArray(response.data.data)) {
//     return response.data.data;
//   }
//   if (response?.data && !Array.isArray(response.data)) return response.data;
//   if (Array.isArray(response?.data)) return response.data[0] || null;
//   if (response?.record) return response.record;
//   if (response?.result) return response.result;
//   if (response?.id || response?._id) return response;
//   return null;
// };

// const getId = (item) => {
//   return item?.id || item?._id || "";
// };

// const getRefId = (value) => {
//   if (!value) return "";
//   if (typeof value === "string") return value;
//   return value.id || value._id || value.$oid || "";
// };

// const getName = (item) => {
//   if (!item) return "-";

//   if (typeof item === "string") return item;

//   return (
//     item.name ||
//     item.title ||
//     item.value ||
//     item.valueAlias ||
//     item.code ||
//     item.subjectName ||
//     item.topicName ||
//     item.unitName ||
//     item.id ||
//     "-"
//   );
// };

// const getQSetIdFromRow = (row = {}) => {
//   return (
//     getRefId(row?.qSet) ||
//     getRefId(row?.qSetId) ||
//     getRefId(row?.qset) ||
//     getRefId(row?.qsetId) ||
//     getRefId(row?.qSet_id) ||
//     getRefId(row?.qset_id) ||
//     ""
//   );
// };

// const buildS3ImageUrl = (imageName, row = {}, client = {}) => {
//   const s3Dir = client?.s3Dir || "";
//   const qSetId = getQSetIdFromRow(row);

//   if (!s3Dir || !qSetId || !imageName) {
//     console.log("S3 image url missing data:", {
//       imageName,
//       s3Dir,
//       qSetId,
//       row,
//       client,
//     });

//     return imageName;
//   }

//   return `https://s3.us-west-004.backblazeb2.com/s3file/${s3Dir}/QSet/${qSetId}/${imageName}`;
// };

// const cleanUnsafeHtml = (html = "") => {
//   return String(html)
//     .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
//     .replace(/\son\w+=["'][^"']*["']/gi, "")
//     .replace(/\sjavascript:/gi, "");
// };

// const normalizeLatex = (value = "") => {
//   return String(value || "")
//     .replace(/\\mathmr\b/g, "\\mathrm")
//     .replace(/\\rm\s*\{/g, "\\mathrm{");
// };

// const renderLatexExpression = (expression, displayMode = false) => {
//   try {
//     return katex.renderToString(normalizeLatex(expression).trim(), {
//       throwOnError: false,
//       displayMode,
//       strict: "ignore",
//       trust: false,
//       output: "html",
//     });
//   } catch (error) {
//     console.warn("KaTeX render failed:", expression, error);
//     return expression;
//   }
// };

// const renderMathInText = (value = "") => {
//   let text = normalizeLatex(value);

//   text = text.replace(/\$\$([\s\S]*?)\$\$/g, (match, expression) =>
//     renderLatexExpression(expression, true),
//   );

//   text = text.replace(/\\\[([\s\S]*?)\\\]/g, (match, expression) =>
//     renderLatexExpression(expression, true),
//   );

//   text = text.replace(/\\\(([\s\S]*?)\\\)/g, (match, expression) =>
//     renderLatexExpression(expression, false),
//   );
//   text = text.replace(/\$([^$\n]+?)\$/g, (match, expression) =>
//     renderLatexExpression(expression, false),
//   );

//   return text;
// };

// const renderTextWithTags = (html = "", row = {}, client = {}) => {
//   if (!html) return "-";

//   let text = cleanUnsafeHtml(html);
//   text = text.replace(
//     /!\[([^\]]*)\]\((https?:\/\/[^\s)]+\.(?:png|jpg|jpeg|webp|gif)(?:\?[^\s)]*)?)\)/gi,
//     (match, alt, src) =>
//       `<img src="${src}" alt="${alt || "Question image"}" class="inline-block max-w-full h-auto my-2" />`,
//   );

//   text = text.replace(
//     /!\[([^\]]*)\]\((image-[\w.-]+\.(?:png|jpg|jpeg|webp|gif))\)/gi,
//     (match, alt, imageName) => {
//       const src = buildS3ImageUrl(imageName, row, client);
//       return `<img src="${src}" alt="${alt || imageName}" class="inline-block max-w-full h-auto my-2" />`;
//     },
//   );
//   text = text.replace(
//     /<img([^>]*?)src=["']([^"']+)["']([^>]*)>/gi,
//     (match, before, oldSrc, after) => {
//       const isFullUrl = /^https?:\/\//i.test(oldSrc);
//       const imageNameMatch = oldSrc.match(
//         /(image-[\w.-]+\.(?:png|jpg|jpeg|webp|gif))(?:\?.*)?$/i,
//       );
//       const src = isFullUrl
//         ? oldSrc
//         : imageNameMatch
//           ? buildS3ImageUrl(imageNameMatch[1], row, client)
//           : oldSrc;

//       return `<img${before}src="${src}"${after} class="inline-block max-w-full h-auto my-2" />`;
//     },
//   );
//   text = text.replace(
//     /(^|[\s>])(image-[\w.-]+\.(?:png|jpg|jpeg|webp|gif))(?=$|[\s<])/gi,
//     (match, prefix, imageName) => {
//       const src = buildS3ImageUrl(imageName, row, client);
//       return `${prefix}<img src="${src}" alt="${imageName}" class="inline-block max-w-full h-auto my-2" />`;
//     },
//   );
//   text = text.replace(
//     /(?<!["'=])(https?:\/\/[^\s<>"']+\.(?:png|jpg|jpeg|webp|gif)(?:\?[^\s<>"']*)?)/gi,
//     (src) =>
//       `<img src="${src}" alt="Question image" class="inline-block max-w-full h-auto my-2" />`,
//   );

//   return renderMathInText(text);
// };

// const normalizeOptions = (value) => {
//   if (!value) return [];

//   if (Array.isArray(value)) {
//     return value
//       .map((item) => {
//         if (item === null || item === undefined) return "";
//         if (typeof item === "string" || typeof item === "number") {
//           return String(item).trim();
//         }
//         return String(
//           item.text ||
//             item.value ||
//             item.option ||
//             item.label ||
//             item.content ||
//             "",
//         ).trim();
//       })
//       .filter(Boolean);
//   }

//   if (typeof value === "string") {
//     const trimmed = value.trim();
//     if (!trimmed) return [];

//     try {
//       const parsed = JSON.parse(trimmed);
//       if (Array.isArray(parsed)) return normalizeOptions(parsed);
//     } catch (error) {}

//     return trimmed
//       .split(/\r?\n|\s*\|\|\s*/)
//       .map((item) => item.trim())
//       .filter(Boolean);
//   }

//   return [];
// };

// const ensureOptionLabel = (option, index) => {
//   const text = String(option || "").trim();
//   if (!text) return "";
//   if (/^\s*(?:\([1-9A-Da-d]\)|\[[1-9A-Da-d]\]|[1-9A-Da-d][.)])\s*/.test(text)) {
//     return text;
//   }

//   return `(${index + 1}) ${text}`;
// };

// const AiQuestionList = () => {
//   const { showError } = useToast();
//   const { client } = useAuthStore();

//   const [loading, setLoading] = useState(false);
//   const [optionLoading, setOptionLoading] = useState(false);

//   const [questions, setQuestions] = useState([]);

//   const [courses, setCourses] = useState([]);
//   const [streams, setStreams] = useState([]);
//   const [subjects, setSubjects] = useState([]);
//   const [subjectUnits, setSubjectUnits] = useState([]);
//   const [topics, setTopics] = useState([]);

//   const referenceCacheRef = useRef({
//     course: {},
//     stream: {},
//     subject: {},
//     subjectunit: {},
//     topic: {},
//   });

//   const referencePromiseRef = useRef({});

//   const [referenceNames, setReferenceNames] = useState({
//     course: {},
//     stream: {},
//     subject: {},
//     subjectunit: {},
//     topic: {},
//   });

//   const [filter, setFilter] = useState(defaultFilter);
//   const [page, setPage] = useState(1);
//   const [pageLimit, setPageLimit] = useState(20);
//   const [total, setTotal] = useState(0);

//   const totalPages = useMemo(() => {
//     return Math.max(Math.ceil(total / pageLimit), 1);
//   }, [total, pageLimit]);

//   useEffect(() => {
//     loadInitialOptions();
//     loadQuestions(defaultFilter, 1, pageLimit);
//   }, []);

//   useEffect(() => {
//     loadQuestions(filter, page, pageLimit);
//   }, [page, pageLimit]);

//   useEffect(() => {
//     if (filter.stream) {
//       loadSubjects(filter.stream);
//     } else {
//       setSubjects([]);
//       setSubjectUnits([]);
//       setTopics([]);
//     }

//     setFilter((prev) => ({
//       ...prev,
//       subject: "",
//       subjectunit: "",
//       topic: "",
//     }));
//   }, [filter.stream]);

//   useEffect(() => {
//     if (filter.subject) {
//       loadSubjectUnits(filter.subject);
//       loadTopics(filter.subject, "");
//     } else {
//       setSubjectUnits([]);
//       setTopics([]);
//     }

//     setFilter((prev) => ({
//       ...prev,
//       subjectunit: "",
//       topic: "",
//     }));
//   }, [filter.subject]);

//   useEffect(() => {
//     if (filter.subject) {
//       loadTopics(filter.subject, filter.subjectunit);
//     }
//   }, [filter.subjectunit]);

//   const mergeReferenceRows = (type, rows = []) => {
//     const cache = referenceCacheRef.current[type] || {};

//     (rows || []).forEach((row) => {
//       const id = String(getId(row) || "");
//       if (!id) return;

//       cache[id] = getName(row);
//     });

//     referenceCacheRef.current[type] = cache;

//     setReferenceNames((prev) => ({
//       ...prev,
//       [type]: { ...cache },
//     }));
//   };

//   const fetchReferenceRecord = async (type, resource, id) => {
//     const finalId = String(id || "");
//     if (!finalId) return null;

//     if (referenceCacheRef.current[type]?.[finalId]) {
//       return referenceCacheRef.current[type][finalId];
//     }

//     const promiseKey = `${type}:${finalId}`;

//     if (!referencePromiseRef.current[promiseKey]) {
//       referencePromiseRef.current[promiseKey] = (async () => {
//         let record = null;

//         try {
//           const response = await apiService.request_old(
//             `/i/ims/${resource}/${encodeURIComponent(finalId)}`,
//             { method: "GET" },
//           );

//           record = getRecord(response);
//         } catch (findOneError) {
//           try {
//             const response = await apiService.request_old(
//               `/i/ims/${resource}?${buildQueryString({
//                 id: finalId,
//                 limit: 1,
//               })}`,
//               { method: "GET" },
//             );

//             record = getRecord(response) || getRows(response)[0] || null;
//           } catch (findError) {
//             console.log(`${resource.toUpperCase()} NAME LOAD FAILED:`, {
//               id: finalId,
//               findOneError,
//               findError,
//             });
//           }
//         }

//         if (record) {
//           mergeReferenceRows(type, [record]);
//           return getName(record);
//         }

//         return "";
//       })().finally(() => {
//         delete referencePromiseRef.current[promiseKey];
//       });
//     }

//     return referencePromiseRef.current[promiseKey];
//   };

//   const loadReferenceNames = async (rows = []) => {
//     const references = {
//       course: {
//         resource: "coption",
//         ids: rows.map((row) => getRefId(row?.course)),
//       },
//       stream: {
//         resource: "coption",
//         ids: rows.map((row) => getRefId(row?.stream)),
//       },
//       subject: {
//         resource: "subject",
//         ids: rows.map((row) => getRefId(row?.subject)),
//       },
//       subjectunit: {
//         resource: "subjectunit",
//         ids: rows.map((row) =>
//           getRefId(row?.subjectunit || row?.subjectUnit || row?.unit),
//         ),
//       },
//       topic: {
//         resource: "topic",
//         ids: rows.map((row) => getRefId(row?.topic)),
//       },
//     };

//     const requests = [];

//     Object.entries(references).forEach(([type, config]) => {
//       const uniqueIds = Array.from(
//         new Set((config.ids || []).filter(Boolean).map(String)),
//       );

//       uniqueIds.forEach((id) => {
//         if (!referenceCacheRef.current[type]?.[id]) {
//           requests.push(fetchReferenceRecord(type, config.resource, id));
//         }
//       });
//     });

//     if (requests.length) {
//       await Promise.allSettled(requests);
//     }
//   };

//   const getReferenceName = (type, value, fallback = "") => {
//     if (!value) return fallback || "-";

//     if (typeof value === "object") {
//       return getName(value);
//     }

//     const id = String(getRefId(value) || value || "");

//     return (
//       referenceNames?.[type]?.[id] ||
//       referenceCacheRef.current?.[type]?.[id] ||
//       fallback ||
//       id ||
//       "-"
//     );
//   };

//   const loadInitialOptions = async () => {
//     try {
//       setOptionLoading(true);

//       const courseApi = `/i/ims/coption?${buildQueryString({
//         option: "COURSE",
//         sort: "seq ASC",
//         limit: "all",
//         status: true,
//       })}`;

//       const streamApi = `/i/ims/coption?${buildQueryString({
//         option: "STREAM",
//         sort: "seq ASC",
//         limit: "all",
//         status: true,
//       })}`;

//       const [courseRes, streamRes] = await Promise.all([
//         apiService.request_old(courseApi, { method: "GET" }),
//         apiService.request_old(streamApi, { method: "GET" }),
//       ]);

//       const courseRows = getRows(courseRes);
//       const streamRows = getRows(streamRes);

//       setCourses(courseRows);
//       setStreams(streamRows);

//       mergeReferenceRows("course", courseRows);
//       mergeReferenceRows("stream", streamRows);
//     } catch (err) {
//       console.log(err);
//       showError("Filter options load failed.");
//     } finally {
//       setOptionLoading(false);
//     }
//   };
//   const loadSubjects = async (streamId) => {
//     try {
//       const api = `/i/ims/subject?${buildQueryString({
//         stream: streamId,
//         limit: "all",
//         populate: "stream",
//       })}`;

//       const res = await apiService.request_old(api, {
//         method: "GET",
//       });

//       const rows = getRows(res);
//       setSubjects(rows);
//       mergeReferenceRows("subject", rows);
//     } catch (err) {
//       console.log(err);
//       showError("Subjects load failed.");
//     }
//   };

//   const loadSubjectUnits = async (subjectId) => {
//     try {
//       const api = `/i/ims/subjectunit?${buildQueryString({
//         subject: subjectId,
//         limit: "all",
//         populate: "subject",
//       })}`;

//       const res = await apiService.request_old(api, {
//         method: "GET",
//       });

//       const rows = getRows(res);
//       setSubjectUnits(rows);
//       mergeReferenceRows("subjectunit", rows);
//     } catch (err) {
//       console.log(err);
//       setSubjectUnits([]);
//     }
//   };

//   const loadTopics = async (subjectId, subjectunitId) => {
//     try {
//       const api = `/i/ims/topic?${buildQueryString({
//         subject: subjectId,
//         subjectunit: subjectunitId,
//         limit: "all",
//         populate: "subject,subjectunit",
//       })}`;

//       const res = await apiService.request_old(api, {
//         method: "GET",
//       });

//       const rows = getRows(res);
//       setTopics(rows);
//       mergeReferenceRows("topic", rows);
//     } catch (err) {
//       console.log(err);
//       setTopics([]);
//     }
//   };

//   const buildQuestionApi = (
//     customFilter = filter,
//     customPage = page,
//     customLimit = pageLimit,
//   ) => {
//     const params = {
//       page: customPage,
//       limit: customLimit,
//       totalCount: true,
//       populate: AI_QUESTION_POPULATE,
//     };

//     Object.keys(customFilter).forEach((key) => {
//       if (customFilter[key]) {
//         params[key] = customFilter[key];
//       }
//     });

//     return `/i/ims/aiquestion?${buildQueryString(params)}`;
//   };

//   const loadQuestions = async (
//     customFilter = filter,
//     customPage = page,
//     customLimit = pageLimit,
//   ) => {
//     try {
//       setLoading(true);

//       const res = await apiService.request(
//         buildQuestionApi(customFilter, customPage, customLimit),
//         { method: "GET" },
//       );

//       const rows = getRows(res);

//       const totalCount =
//         res?.totalCount ||
//         res?.data?.totalCount ||
//         res?.total ||
//         res?.data?.total ||
//         res?.count ||
//         res?.data?.count ||
//         rows.length;

//       setQuestions(rows);
//       setTotal(Number(totalCount || 0));

//       await loadReferenceNames(rows);
//     } catch (err) {
//       console.log(err);
//       showError(err?.message || "AI questions load failed.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleFilterChange = (key, value) => {
//     setFilter((prev) => ({
//       ...prev,
//       [key]: value,
//     }));
//   };

//   const applyFilter = () => {
//     setPage(1);
//     loadQuestions(filter, 1, pageLimit);
//   };

//   const resetFilter = () => {
//     const cleanFilter = { ...defaultFilter };

//     setFilter(cleanFilter);
//     setSubjects([]);
//     setSubjectUnits([]);
//     setTopics([]);
//     setPage(1);

//     loadQuestions(cleanFilter, 1, pageLimit);
//   };

//   const renderQuestionText = (html, row = {}) => {
//     if (!html) return "-";

//     return (
//       <div
//         className="question-html max-w-none overflow-visible whitespace-normal"
//         style={{ maxHeight: "none", overflow: "visible" }}
//         dangerouslySetInnerHTML={{
//           __html: renderTextWithTags(html, row, client),
//         }}
//       />
//     );
//   };

//   const renderOptions = (options, row = {}, language = "eng") => {
//     const optionList = normalizeOptions(options);
//     if (!optionList.length) return null;

//     return (
//       <div
//         className={`mt-2 space-y-1 ${
//           language === "hin" ? "text-gray-500" : "text-gray-800"
//         }`}
//       >
//         {optionList.map((option, index) => (
//           <div key={`${language}-${index}`} className="flex items-start gap-1">
//             {renderQuestionText(ensureOptionLabel(option, index), row)}
//           </div>
//         ))}
//       </div>
//     );
//   };

//   return (
//     <div className="p-4">
//       <div className="flex justify-between items-center mb-5">
//         <div>
//           <h1 className="text-2xl font-bold">AI Questions</h1>
//           <p className="text-sm text-gray-500">Total Records: {total}</p>
//         </div>

//         <button
//           type="button"
//           onClick={() => loadQuestions(filter, page, pageLimit)}
//           disabled={loading}
//           className="inline-flex items-center gap-2 px-4 py-2 rounded bg-gray-800 text-white disabled:bg-gray-400"
//         >
//           <FaSyncAlt className="h-3 w-3" />
//           Refresh
//         </button>
//       </div>

//       {/* Filter section agar chahiye to uncomment kar lena */}
//       {/*
//       <div className="bg-white rounded-lg shadow-md p-4 mb-5">
//         <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
//           <div>
//             <label className="block text-sm mb-1">Search</label>
//             <input
//               type="text"
//               value={filter.search}
//               onChange={(e) => handleFilterChange("search", e.target.value)}
//               placeholder="Question / answer search"
//               className="w-full border rounded p-2"
//             />
//           </div>

//           <div>
//             <label className="block text-sm mb-1">Course</label>
//             <select
//               value={filter.course}
//               onChange={(e) => handleFilterChange("course", e.target.value)}
//               className="w-full border rounded p-2"
//             >
//               <option value="">All Course</option>

//               {courses.map((item) => (
//                 <option key={getId(item)} value={getId(item)}>
//                   {getName(item)}
//                 </option>
//               ))}
//             </select>
//           </div>

//           <div>
//             <label className="block text-sm mb-1">Stream</label>
//             <select
//               value={filter.stream}
//               onChange={(e) => handleFilterChange("stream", e.target.value)}
//               className="w-full border rounded p-2"
//             >
//               <option value="">All Stream</option>

//               {streams.map((item) => (
//                 <option key={getId(item)} value={getId(item)}>
//                   {getName(item)}
//                 </option>
//               ))}
//             </select>
//           </div>

//           <div>
//             <label className="block text-sm mb-1">Subject</label>
//             <select
//               value={filter.subject}
//               disabled={!filter.stream}
//               onChange={(e) => handleFilterChange("subject", e.target.value)}
//               className="w-full border rounded p-2 disabled:bg-gray-100"
//             >
//               <option value="">All Subject</option>

//               {subjects.map((item) => (
//                 <option key={getId(item)} value={getId(item)}>
//                   {getName(item)}
//                 </option>
//               ))}
//             </select>
//           </div>

//           <div>
//             <label className="block text-sm mb-1">Subject Unit</label>
//             <select
//               value={filter.subjectunit}
//               disabled={!filter.subject}
//               onChange={(e) =>
//                 handleFilterChange("subjectunit", e.target.value)
//               }
//               className="w-full border rounded p-2 disabled:bg-gray-100"
//             >
//               <option value="">All Unit</option>

//               {subjectUnits.map((item) => (
//                 <option key={getId(item)} value={getId(item)}>
//                   {getName(item)}
//                 </option>
//               ))}
//             </select>
//           </div>

//           <div>
//             <label className="block text-sm mb-1">Topic</label>
//             <select
//               value={filter.topic}
//               disabled={!filter.subject}
//               onChange={(e) => handleFilterChange("topic", e.target.value)}
//               className="w-full border rounded p-2 disabled:bg-gray-100"
//             >
//               <option value="">All Topic</option>

//               {topics.map((item) => (
//                 <option key={getId(item)} value={getId(item)}>
//                   {getName(item)}
//                 </option>
//               ))}
//             </select>
//           </div>

//           <div>
//             <label className="block text-sm mb-1">Status</label>
//             <select
//               value={filter.status}
//               onChange={(e) => handleFilterChange("status", e.target.value)}
//               className="w-full border rounded p-2"
//             >
//               <option value="">All Status</option>
//               <option value="true">Active</option>
//               <option value="false">Inactive</option>
//               <option value="Pending">Pending</option>
//               <option value="Approved">Approved</option>
//               <option value="Rejected">Rejected</option>
//             </select>
//           </div>

//           <div>
//             <label className="block text-sm mb-1">Table Limit</label>
//             <select
//               value={pageLimit}
//               onChange={(e) => {
//                 const newLimit = Number(e.target.value);
//                 setPageLimit(newLimit);
//                 setPage(1);
//                 loadQuestions(filter, 1, newLimit);
//               }}
//               className="w-full border rounded p-2"
//             >
//               <option value={10}>10</option>
//               <option value={20}>20</option>
//               <option value={50}>50</option>
//               <option value={100}>100</option>
//             </select>
//           </div>
//         </div>

//         <div className="flex items-center gap-3 mt-4">
//           <button
//             type="button"
//             onClick={applyFilter}
//             disabled={loading || optionLoading}
//             className="inline-flex items-center gap-2 px-4 py-2 rounded bg-blue-600 text-white disabled:bg-gray-400"
//           >
//             <FaSearch className="h-3 w-3" />
//             Search
//           </button>

//           <button
//             type="button"
//             onClick={resetFilter}
//             disabled={loading}
//             className="inline-flex items-center gap-2 px-4 py-2 rounded bg-gray-500 text-white disabled:bg-gray-400"
//           >
//             <FaTimes className="h-3 w-3" />
//             Reset
//           </button>

//           {(loading || optionLoading) && <Loader />}
//         </div>
//       </div>
//       */}

//       <div className="bg-white rounded-lg shadow-md overflow-hidden">
//         <div className="overflow-x-auto">
//           <table className="w-full border text-sm">
//             <thead className="bg-gray-100">
//               <tr>
//                 <th className="border p-2 text-left w-[70px]">S.No.</th>
//                 <th className="border p-2 text-left w-[90px]">Q.No.</th>
//                 <th className="border p-2 text-left min-w-[560px]">Question</th>
//                 <th className="border p-2 text-left min-w-[130px]">Course</th>
//                 <th className="border p-2 text-left min-w-[130px]">Stream</th>
//                 <th className="border p-2 text-left min-w-[130px]">Subject</th>
//                 <th className="border p-2 text-left min-w-[130px]">AiUnit</th>
//                 <th className="border p-2 text-left min-w-[130px]">AiTopic</th>
//                 <th className="border p-2 text-left min-w-[100px]">Answer</th>
//                 <th className="border p-2 text-left min-w-[110px]">Status</th>
//               </tr>
//             </thead>

//             <tbody>
//               {loading ? (
//                 <tr>
//                   <td colSpan="10" className="border p-6 text-center">
//                     Loading...
//                   </td>
//                 </tr>
//               ) : questions.length === 0 ? (
//                 <tr>
//                   <td colSpan="10" className="border p-6 text-center">
//                     No AI questions found.
//                   </td>
//                 </tr>
//               ) : (
//                 questions.map((row, index) => {
//                   const unit = row.subjectunit || row.subjectUnit || row.unit;

//                   return (
//                     <tr key={row.id || row._id || index}>
//                       <td className="border p-2">
//                         {(page - 1) * pageLimit + index + 1}
//                       </td>

//                       <td className="border p-2 font-semibold">
//                         {row.question_num || row.questionNo || row.qNo || "-"}
//                       </td>

//                       <td className="border p-2 align-top">
//                         {(row.question_eng ||
//                           row.question ||
//                           row.questionText) && (
//                           <div className="font-medium">
//                             {renderQuestionText(
//                               row.question_eng ||
//                                 row.question ||
//                                 row.questionText,
//                               row,
//                             )}

//                             {renderOptions(
//                               row.options_eng ||
//                                 row.option_eng ||
//                                 row.englishOptions ||
//                                 row.optionEng ||
//                                 row.optionsEnglish ||
//                                 row.options,
//                               row,
//                               "eng",
//                             )}
//                           </div>
//                         )}

//                         {row.question_hin && (
//                           <div className="text-gray-500 mt-3 pt-2 border-t border-gray-200">
//                             {renderQuestionText(row.question_hin, row)}

//                             {renderOptions(
//                               row.options_hin ||
//                                 row.option_hin ||
//                                 row.hindiOptions ||
//                                 row.optionHin ||
//                                 row.optionsHindi,
//                               row,
//                               "hin",
//                             )}
//                           </div>
//                         )}
//                       </td>

//                       <td className="border p-2">
//                         {getReferenceName("course", row.course, row.courseName)}
//                       </td>

//                       <td className="border p-2">
//                         {getReferenceName("stream", row.stream, row.streamName)}
//                       </td>

//                       <td className="border p-2">
//                         {getReferenceName(
//                           "subject",
//                           row.subject,
//                           row.subjectName,
//                         )}
//                       </td>

//                       <td className="border p-2">
//                         {getReferenceName(
//                           "subjectunit",
//                           unit,
//                           row.aiUnitName || row.unitName,
//                         )}
//                       </td>

//                       <td className="border p-2">
//                         {getReferenceName(
//                           "topic",
//                           row.topic,
//                           row.aiTopicName || row.topicName,
//                         )}
//                       </td>

//                       <td className="border p-2 font-semibold">
//                         {row.answer || row.correctOption || "-"}
//                       </td>

//                       <td className="border p-2">
//                         {row.status === true
//                           ? "Active"
//                           : row.status === false
//                             ? "Inactive"
//                             : row.status || "-"}
//                       </td>
//                     </tr>
//                   );
//                 })
//               )}
//             </tbody>
//           </table>
//         </div>

//         <div className="flex justify-between items-center p-4 border-t">
//           <div className="text-sm text-gray-600">
//             Page {page} / {totalPages} | Total {total}
//           </div>

//           <div className="flex gap-2">
//             <button
//               type="button"
//               disabled={page <= 1 || loading}
//               onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
//               className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
//             >
//               Previous
//             </button>

//             <button
//               type="button"
//               disabled={page >= totalPages || loading}
//               onClick={() => setPage((prev) => prev + 1)}
//               className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
//             >
//               Next
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AiQuestionList;
